import { useState, useRef, useEffect } from 'react'
import { FiPlay, FiPause, FiVolume2, FiVolumeX } from 'react-icons/fi'
import axios from 'axios'

export default function AudioPlayer({ src, filename, formStyle, textStyle, buttonStyle, hoverButtonStyle, isHovered = false, websiteId, websiteSlug, sectionType = 'audio' }) {
  const audioRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  const [audioError, setAudioError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  // Log audio playback event
  const logAudioPlayback = (action, metadata = {}) => {
    try {
      axios.post('/websites-public/log/audio-playback', {
        websiteId: websiteId || 'unknown',
        websiteSlug: websiteSlug || 'unknown',
        audioFileName: filename || 'unknown',
        action: action, // 'play', 'pause', 'seek', 'volumechange', 'mute', 'error', 'loaded'
        currentTime: audioRef.current?.currentTime || 0,
        duration: audioRef.current?.duration || 0,
        timestamp: new Date().toISOString(),
        ...metadata
      }).catch(err => console.warn('[AudioPlayer] Failed to log event:', err.message))
    } catch (err) {
      console.warn('[AudioPlayer] Error logging playback event:', err.message)
    }
  }

  const getAudioMimeType = () => {
    if (filename) {
      const lower = filename.toLowerCase()
      if (lower.endsWith('.ogg')) return 'audio/ogg'
      if (lower.endsWith('.wav')) return 'audio/wav'
      if (lower.endsWith('.mp3')) return 'audio/mpeg'
      if (lower.endsWith('.m4a') || lower.endsWith('.mp4') || lower.endsWith('.m4b') || lower.endsWith('.m4p')) return 'audio/mp4'
      if (lower.endsWith('.aac')) return 'audio/aac'
      if (lower.endsWith('.webm')) return 'audio/webm'
      if (lower.endsWith('.flac')) return 'audio/flac'
    }

    if (src?.startsWith('data:')) {
      const match = src.match(/^data:([^;]+);/)
      if (match) return match[1]
    }

    return 'audio/mpeg'
  }

  const handlePlayPause = () => {
    if (!audioRef.current) {
      console.error('[AudioPlayer] Audio ref not available')
      return
    }
    
    if (audioError) {
      console.error('[AudioPlayer] Cannot play - audio has error:', audioError)
      logAudioPlayback('play_error', { error: audioError, src: src ? 'provided' : 'missing' })
      return
    }
    
    if (isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
      logAudioPlayback('pause')
      console.log('[AudioPlayer] Paused audio:', filename)
      return
    }

    console.log('[AudioPlayer] Playing audio:', filename, 'Duration:', duration)
    const playPromise = audioRef.current.play()
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          setIsPlaying(true)
          logAudioPlayback('play')
        })
        .catch(err => {
          console.error('[AudioPlayer] Error playing audio:', err.message)
          setAudioError(err.message)
          setIsPlaying(false)
          logAudioPlayback('play_failed', { error: err.message, duration, filesize: src?.length })
        })
    } else {
      setIsPlaying(true)
      logAudioPlayback('play')
    }
  }

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime)
    }
  }

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      const dur = audioRef.current.duration
      setDuration(dur)
      setIsLoading(false)
      console.log('[AudioPlayer] Audio metadata loaded:', {
        filename,
        duration: dur,
        src_length: src?.length || 0,
        src_preview: src ? src.substring(0, 100) : 'no src',
        audioType: getAudioMimeType()
      })
      logAudioPlayback('loaded', { duration: dur, src_length: src?.length || 0 })
    }
  }

  const handleCanPlay = () => {
    if (isLoading) {
      setIsLoading(false)
    }
    logAudioPlayback('can_play', { src_length: src?.length || 0 })
  }

  const handleProgressChange = (e) => {
    const newTime = parseFloat(e.target.value)
    if (audioRef.current) {
      audioRef.current.currentTime = newTime
      setCurrentTime(newTime)
      logAudioPlayback('seek', { seekTime: newTime })
    }
  }

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value)
    setVolume(newVolume)
    if (audioRef.current) {
      audioRef.current.volume = newVolume
      logAudioPlayback('volumechange', { volume: newVolume })
    }
  }

  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = volume
        setIsMuted(false)
        logAudioPlayback('unmute')
      } else {
        audioRef.current.volume = 0
        setIsMuted(true)
        logAudioPlayback('mute')
      }
    }
  }

  const handleAudioError = (event) => {
    const error = event.target.error
    let errorMsg = 'Unknown error'
    
    if (error) {
      switch (error.code) {
        case error.MEDIA_ERR_ABORTED:
          errorMsg = 'Audio playback was aborted'
          break
        case error.MEDIA_ERR_NETWORK:
          errorMsg = 'Network error - could not load audio'
          break
        case error.MEDIA_ERR_DECODE:
          errorMsg = 'Audio file is corrupted or invalid format'
          break
        case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
          errorMsg = 'Audio format not supported by browser'
          break
        default:
          errorMsg = `Audio error code: ${error.code}`
      }
    }
    
    console.error('[AudioPlayer] Audio element error:', {
      filename,
      errorCode: error?.code,
      errorMessage: errorMsg,
      src_length: src?.length || 0,
      src_preview: src ? src.substring(0, 100) : 'no src',
      audioType: getAudioMimeType()
    })
    
    setAudioError(errorMsg)
    setIsPlaying(false)
    logAudioPlayback('audio_error', { 
      errorCode: error?.code, 
      errorMessage: errorMsg,
      src_length: src?.length || 0
    })
  }

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00'
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
  }

  useEffect(() => {
    const handleEnded = () => {
      setIsPlaying(false)
      console.log('[AudioPlayer] Audio ended:', filename)
      logAudioPlayback('ended')
    }
    
    const audio = audioRef.current
    if (audio) {
      audio.addEventListener('ended', handleEnded)
      // Also listen for play to log when actually starts playing
      const handlePlay = () => console.log('[AudioPlayer] Audio started playing:', filename)
      audio.addEventListener('play', handlePlay)
      
      return () => {
        audio.removeEventListener('ended', handleEnded)
        audio.removeEventListener('play', handlePlay)
      }
    }
  }, [filename])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    if (src) {
      console.log('[AudioPlayer] Audio source loaded:', {
        filename,
        src_length: src.length,
        src_preview: src.substring(0, 100),
        src_starts_with_data: src.startsWith('data:')
      })
      setIsLoading(true)
      setAudioError(null)
      setCurrentTime(0)
      setDuration(0)
      setIsPlaying(false)
      audio.load()
    } else {
      console.warn('[AudioPlayer] No audio source provided for:', filename)
      setAudioError('No audio file provided')
      setIsLoading(false)
      setIsPlaying(false)
    }
  }, [src, filename])

  return (
    <div 
      className="w-full rounded-lg p-4 transition-colors duration-200"
      style={isHovered && hoverButtonStyle ? hoverButtonStyle : (formStyle || { backgroundColor: '#F3F4F6' })}
    >
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onCanPlay={handleCanPlay}
        onError={handleAudioError}
        preload="metadata"
      />

      <div className="space-y-3">
        {/* Filename */}
        <div className="text-sm font-medium truncate" style={textStyle || { color: '#374151' }}>
          {filename || 'Audio file'}
        </div>

        {/* Error Display */}
        {audioError && (
          <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700">
            <strong>⚠️ Error:</strong> {audioError}
          </div>
        )}

        {/* Play/Pause + Volume Controls */}
        <div className="flex items-center gap-3">
          {/* Play/Pause Button */}
          <button
            onClick={handlePlayPause}
            disabled={audioError || isLoading}
            className="flex-shrink-0 p-2 rounded-lg transition-colors duration-200 hover:opacity-80 disabled:opacity-50"
            style={buttonStyle || { backgroundColor: '#3B82F6', color: '#FFFFFF' }}
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isLoading ? (
              <span className="text-xs">⏳</span>
            ) : isPlaying ? (
              <FiPause className="w-5 h-5" />
            ) : (
              <FiPlay className="w-5 h-5" />
            )}
          </button>

          {/* Time Display */}
          <div className="text-xs font-medium whitespace-nowrap" style={textStyle || { color: '#374151' }}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>

          {/* Mute Button */}
          <button
            onClick={toggleMute}
            className="flex-shrink-0 p-2 rounded-lg transition-colors duration-200 hover:opacity-80"
            style={buttonStyle || { backgroundColor: '#3B82F6', color: '#FFFFFF' }}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? (
              <FiVolumeX className="w-4 h-4" />
            ) : (
              <FiVolume2 className="w-4 h-4" />
            )}
          </button>

          {/* Volume Slider */}
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="flex-shrink-0 w-20 h-2 rounded-lg appearance-none cursor-pointer accent-blue-500"
            title="Volume"
            style={{
              backgroundColor: '#E5E7EB',
              WebkitAppearance: 'slider-horizontal'
            }}
          />
        </div>

        {/* Progress Bar */}
        <div className="flex items-center gap-2">
          <input
            type="range"
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={handleProgressChange}
            className="flex-1 h-2 rounded-lg appearance-none cursor-pointer accent-blue-500"
            title="Seek"
            style={{
              backgroundColor: '#D1D5DB',
              WebkitAppearance: 'slider-horizontal'
            }}
          />
        </div>
      </div>
    </div>
  )
}
